import { useState, useRef } from 'react';
import {
  Avatar,
  Collapse,
  useDisclosure,
  Icon,
  CloseButton,
  useToast,
} from '@chakra-ui/react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useMomentsContext } from './MomentsContext';

import { H1, Paragraph } from '../../components/Typography';
import ArrowUp from '../../components/Icons/ArrowUp';
import ArrowDown from '../../components/Icons/ArrowDown';
import Button from '../../components/Button';
import { MomentApplication } from './moments.types';
import { memo } from 'react';

export const MomentsBanner = memo((): JSX.Element => {
  const {
    momentsForClaim,
    momentsForDismiss,
    handleClaimReward,
    dismissReward,
    isClaiming,
  } = useMomentsContext();
  const { t } = useTranslation();
  const claimToast = useToast();

  const claimed = useRef(false);
  // Claiming transition started
  if (isClaiming) {
    claimed.current = true;
  }

  // One of the banners was dismissed
  const [dismissedMoments, setDismissedMoments] = useState([]);
  const afterDismiss = (momentId: string) => {
    setDismissedMoments([...dismissedMoments, momentId]);
  };

  // Claiming transition finished
  if (!isClaiming && claimed.current) {
    claimed.current = false;
    claimToast({
      status: 'success',
      isClosable: true,
      position: 'top',
      title: t('moments.claimToast.title'),
    });
  }

  const momentsToDisplay = [...momentsForClaim, ...momentsForDismiss];
  if (isEmpty(momentsToDisplay)) {
    return null;
  }

  const visibleMoments = momentsToDisplay.length - dismissedMoments.length;
  if (visibleMoments === 0) {
    return null;
  } else if (visibleMoments === 1) {
    const momentBanner = momentsToDisplay.filter(
      (momentApp) => !dismissedMoments.includes(momentApp.moment.id),
    )[0];

    return (
      momentBanner && (
        <BannerElement>
          <Banner
            momentApplication={momentBanner}
            claimAction={handleClaimReward}
            dismiss={dismissReward}
            afterDismiss={afterDismiss}
            isClaiming={isClaiming}
          />
        </BannerElement>
      )
    );
  }

  return (
    <BannerStack
      banners={momentsToDisplay}
      claimAction={handleClaimReward}
      dismiss={dismissReward}
      afterDismiss={afterDismiss}
      isClaiming={isClaiming}
    />
  );
});

const BannerStack = ({
  banners,
  claimAction,
  dismiss,
  afterDismiss,
  isClaiming,
}: {
  banners: MomentApplication[];
  claimAction: (moment: MomentApplication) => void;
  dismiss: (moment: MomentApplication['moment']) => void;
  afterDismiss?: (momentId: string) => void;
  isClaiming: boolean;
}) => {
  const { isOpen, onToggle } = useDisclosure();

  if (isEmpty(banners)) {
    return null;
  }

  return (
    <BannersStack>
      <Collapse in={isOpen} startingHeight={145} animateOpacity>
        <div className="awt-banners-list">
          {banners.map((momentApplication) => (
            <Banner
              key={momentApplication.id}
              momentApplication={momentApplication}
              claimAction={claimAction}
              dismiss={dismiss}
              afterDismiss={afterDismiss}
              isClaiming={isClaiming}
            />
          ))}
        </div>
      </Collapse>
      <Button fullWidth onClick={onToggle}>
        <Icon as={isOpen ? ArrowUp : ArrowDown} />
      </Button>
    </BannersStack>
  );
};
const Banner = ({
  momentApplication,
  claimAction,
  dismiss,
  afterDismiss,
  isClaiming,
}: {
  momentApplication: MomentApplication;
  claimAction: (moment: MomentApplication) => void;
  dismiss: (moment: MomentApplication['moment']) => void;
  afterDismiss?: (momentId: string) => void;
  isClaiming: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const [shouldDisplay, setShouldDisplay] = useState(true);
  const { moment } = momentApplication;
  const { claimBannerMetadata } = moment;
  const isClaimable = momentApplication.canBeClaimed;
  const hasAvatar = !!claimBannerMetadata?.graphicSrc;

  if (isEmpty(claimBannerMetadata) || !shouldDisplay) {
    return null;
  }

  return (
    <BannerContainer>
      {hasAvatar && (
        <Avatar
          showBorder={false}
          borderColor="transparent"
          backgroundColor="transparent"
          size="lg"
          className="awt-moment-banner-avatar"
          src={claimBannerMetadata.graphicSrc}
        />
      )}

      <main>
        <H1 modifiers="heading">{claimBannerMetadata.title}</H1>
        <Paragraph modifiers="body">
          {claimBannerMetadata.description}
        </Paragraph>
      </main>
      <aside>
        {isClaimable && (
          <Button
            data-testid="claim-button"
            onClick={() => claimAction(momentApplication)}
            outlined
            disabled={isClaiming}
          >
            {claimBannerMetadata.claimBtnText || t('moments.claimReward')}
          </Button>
        )}
        <CloseButton
          size="sm"
          onClick={() => {
            dismiss({ ...moment, canBeClaimed: isClaimable });
            setShouldDisplay(false);

            afterDismiss?.(moment.id);
          }}
          data-testid="dismiss-button"
          className="awt-dismiss-button"
        />
      </aside>
    </BannerContainer>
  );
};

const BannerElement = styled.div.attrs({ className: 'awt-banner' })`
  width: 100%;
  margin: 2rem 0;
  font-size: 16px;
`;

const BannersStack = styled.div.attrs({ className: 'awt-banners-stack' })`
  .awt-banners-list {
    display: flex;
    flex-direction: column;
    padding: 1px;
    gap: 1.4em;
    transition: all 0.2s ease-in-out;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const BannerContainer = styled.article.attrs({
  className: 'awt-banner-container',
})`
  width: 100%;
  display: flex;
  min-height: 100px;
  justify-content: space-between;
  align-items: center;
  padding: 1.2em;
  border-radius: 4px;
  border: 1px solid inherit;
  box-shadow:
    0px 2px 1px rgba(0, 0, 0, 0.05),
    0px 0px 1px rgba(0, 0, 0, 0.25);

  &:hover {
    box-shadow:
      0px 4px 2px rgba(0, 0, 0, 0.05),
      0px 2px 3px rgba(0, 0, 0, 0.25);
  }

  gap: 2rem;
  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 0.8em;
  }

  aside {
    display: flex;
    min-width: fit-content;
    justify-content: space-between;
    align-items: center;
    gap: 1em;
  }

  @media (max-width: 680px) {
    flex-direction: column;
    position: relative;
    gap: 1em;
    main {
      flex: 1 1 auto;
      align-items: center;
      justify-content: center;
      gap: 0.8em;
      order: 2;
      text-align: center;
      text-wrap: balance;
    }

    aside {
      flex: 1 1 auto;
      order: 1;
      .awt-dismiss-button {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        cursor: pointer;
      }
    }
  }
`;
